import { Component, HostListener, OnInit, CUSTOM_ELEMENTS_SCHEMA } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { InfiniteScrollModule } from 'ngx-infinite-scroll';
import { auditTime, debounceTime, distinctUntilChanged, Subject } from 'rxjs';
import { ApiService } from 'src/app/services/api.service';
import { AppSettings } from 'src/app/services/AppSettings';
import { CommonValues } from 'src/app/services/commonValues';
import { PageType } from 'src/app/services/pageTypes';
import { UtilService } from 'src/app/services/util.service';
import { JobCreationTriggerService } from 'src/app/services/job-creation-trigger.service';
import { JobService } from 'src/app/services/job.service';
import { JobsComponent } from '../../job-module/job/job.component';
import { NG_ICON_DIRECTIVES } from '@ng-icons/core';
import { CommonSkeletonComponent } from '../../../skeleton/common-skeleton/common-skeleton.component';


@Component({
  selector: 'app-business-jobs',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    InfiniteScrollModule,
JobsComponent
  ,
    NG_ICON_DIRECTIVES,
    CommonSkeletonComponent,
  ],
  templateUrl: './business-jobs.component.html',
  styleUrls: ['./business-jobs.component.scss']
})
export class BusinessJobsComponent implements OnInit {
  showBusinessJobs: any = [];
  response: any = [];
  stopscrollFlag: boolean = false
  creditexpiredflag: boolean = false
  jobId: any;
  searchContent: any;
  loadAPIcall: boolean = false;
  // Wireframe rows (app-jobs viewSize 'business-row'). Set false to bring back the
  // 'large' job cards exactly as before.
  useWireframeJobs = true;
  businessId: any;
  // Latest query params; the admin check below reads isAdmin / isSuperAdmin /
  // viewingAsMember from them, the same flags the rest of the business page uses.
  businessParams: any = {};
  constructor(
    private api: ApiService,
    private commonvalues: CommonValues,
    private route: ActivatedRoute,
    private util: UtilService,
    private pageType: PageType,
    private jobCreationTrigger: JobCreationTriggerService,
    private jobService: JobService) {

  }

  // Soft update after a job is edited from a row. app-job-template emits
  // { jobId, status } once jobs/updateJob succeeds (re-emitted by app-jobs as
  // jobReloadFromJobComponent); refetch just that job and patch its row in place instead
  // of reloading the list, so paging and scroll position stay as they are.
  onJobEdited(event: any): void {
    const jobId = event?.jobId;
    if (!jobId) {
      return;
    }
    this.jobService.findByJobId(jobId).subscribe({
      next: (data: any) => {
        // jobs/findJobById returns the job under data.jobData (see the widgets that use it).
        const fresh = data?.jobData;
        const index = this.response.findIndex((job: any) => job.jobId == jobId);
        if (!fresh || index < 0) {
          return;
        }
        const row = this.response[index];
        // Only keys the response actually carries replace the row's, so list-only fields
        // (counts etc.) keep their current values.
        const updated: any = { ...row };
        Object.keys(fresh).forEach((key) => {
          if (fresh[key] !== undefined) {
            updated[key] = fresh[key];
          }
        });
        // Poster photo, prepared the way getJobList() prepares it: a bare file id gets the
        // download prefix; when the response has none, keep the row's existing URL.
        if (row.user || fresh.user) {
          const photo = fresh.user?.photo;
          updated.user = {
            ...(row.user || {}),
            ...(fresh.user || {}),
            photo: photo ? (photo.startsWith('http') || photo.startsWith(AppSettings.photoUrl) ? photo : AppSettings.photoUrl + photo)
              : row.user?.photo,
          };
        }
        updated.activityDate = updated.updatedOn;
        // Same rule as the list: only ACTIVE jobs are shown, so an edit that changes the
        // status drops the row.
        const next = [...this.response];
        if (updated.status != 'ACTIVE') {
          next.splice(index, 1);
        } else {
          next[index] = updated;
        }
        this.response = next;
        this.commonvalues.setjobsData(this.response);
      },
      // On failure keep the row as it is; the edit itself already succeeded.
      error: () => { }
    });
  }

  // Admin View / Member View toggle on the business page (banner sets viewingAsMember).
  get isMemberPreview(): boolean {
    return (this.businessParams || {}).viewingAsMember == 'true';
  }

  // Admin of this business and not previewing as a member.
  get canPostJob(): boolean {
    const p = this.businessParams || {};
    return (p.isSuperAdmin == 'true' || p.isAdmin == 'true') && p.viewingAsMember != 'true';
  }

  // getJobList() sets this exact text when the API returns no jobs (no search or a search).
  get noJobsFound(): boolean {
    return this.jobFoundStatus == "Couldn't find any Jobs.";
  }

  // Same entry point as the Post a Job quick action (BusinessComponent.postJob).
  postJob(): void {
    this.jobCreationTrigger.trigger('JOBS');
  }

  // ✕ in the search pill: empty the box and reload through the existing search path.
  clearSearch(): void {
    this.searchContent = '';
    this.searchfilter('');
  }

  ngOnInit() {
    // Get businessId from query params OR localStorage for standalone Job Center module
    this.route.queryParams.subscribe((res) => {
      this.businessParams = res || {};
      let businessId: any;
      let jobId: any;
      if (res && res.businessId) {
        businessId = res.businessId;
        jobId = res.jobId;
      } else {
        // Standalone mode - get businessId from localStorage
        businessId = localStorage.getItem('businessId');
        jobId = res?.jobId || '';
      }
      // queryParams re-emits on unrelated param changes (e.g. the login
      // redirect's data blob, currentPath/viewingAsMember merges). Only reload
      // when the list's inputs changed, and start paging from scratch —
      // otherwise a second concurrent first-page call returns the same cursor
      // and trips stopscrollFlag, killing the infinite scroll.
      if (this.listLoaded && businessId == this.businessId && (jobId || '') == (this.jobId || '')) {
        return;
      }
      this.listLoaded = true;
      this.businessId = businessId;
      this.jobId = jobId;
      this.searchContent = jobId;
      this.response = [];
      this.obj.searchAfterKey = null;
      this.previousSearchAfterKey = null;
      this.previousSearchAfterKey1 = null;
      this.stopscrollFlag = false;
      this.jobFoundStatus = "Fetching Jobs...";
      this.getJobList();
    })

    // search filter
    this.searchObserver.pipe(
      auditTime(500),
      distinctUntilChanged(),
      debounceTime(1000),
    ).subscribe((res: string) => {
      if (res != "") {
        this.jobId = this.searchContent;
        this.previousSearchAfterKey = null;
        this.previousSearchAfterKey1 = null;
        this.obj.searchAfterKey = null;
        this.response = [];
        this.getJobList()
      }
      else if (res === null) {
        this.jobId = this.searchContent;

        this.previousSearchAfterKey = null;
        this.previousSearchAfterKey1 = null;
        this.obj.searchAfterKey = null;
        this.response = [];
        this.getJobList()
      }
    });
  }

  searchfilter(val: string) {
    this.searchObserver.next(val === "" ? null : val);
  }
  searchObserver = new Subject<string>();

  getdatafromjob(data: any) {
    data.userId = localStorage.getItem('userId')
    data.pageNumber = 0;
    data.limit = 10;
    this.api.create("candidates/findSuggestedCandidates", data).subscribe((res) => {
      this.util.stopLoader();
      res.componentName = 'SuggestedCandidateComponent';
      this.showBusinessJobs = res;
      // this.pageType.setsuggjob(res)
    })
  }

  img: any;
  obj: any = {
    limit: 10,
    searchAfterKey: null,
    searchContent: "",
    userId: localStorage.getItem('userId'),

  }
  jobFoundStatus: any = "Fetching Jobs..."
  previousSearchAfterKey: any = null;
  previousSearchAfterKey1 = null;
  listLoaded: boolean = false;
  connectionJobs: boolean = false;
  totalJobsCount: any;
  getJobList() {
    if (this.jobId) {
      this.obj.searchContent = this.jobId;
    } else {
      this.obj.searchContent = '';
    }

    //this.util.startLoader()
    this.loadAPIcall = true;
    this.obj.businessId = this.businessId;
    this.api.create("jobs/filter", this.obj).subscribe((res) => {
      this.loadAPIcall = false;
      this.util.stopLoader();
      if (res.data.totalJobsCount != undefined) {
        this.totalJobsCount = res.data.totalJobsCount;
      }

      // A short page is the last page: stop here instead of one more call that
      // returns 0. Raw server count, before non-ACTIVE jobs are dropped below.
      if (res.code == "00000" && (res.data.jobList?.length || 0) < this.obj.limit) {
        this.stopscrollFlag = true;
      }

      if (res.code == "00000" && res.data.jobList.length > 0) {
        this.connectionJobs = true;
        this.jobFoundStatus = "Fetching Jobs...";

        // searchAfterKey is a multi-value cursor (one value per sort field);
        // pass and compare the whole array, not just [0], or ties on the
        // first sort value repeat/skip jobs and falsely stop the scroll.
        const searchAfterKey = JSON.stringify(res.data.searchAfterKey);
        if ((searchAfterKey != this.previousSearchAfterKey) || this.obj.searchAfterKey == null) {
          if (res.data.searchAfterKey[0] < 0) {
            this.obj.searchAfterKey = res.data.searchAfterKey;
            this.previousSearchAfterKey = searchAfterKey;
            this.stopscrollFlag = true
          } else if (res.data.searchAfterKey[0] > 0) {
            this.obj.searchAfterKey = res.data.searchAfterKey;
            this.previousSearchAfterKey = searchAfterKey;
          }
          res.data.jobList.forEach(ele => {
            if (ele.user.photo != null) {
              ele.user.photo = AppSettings.photoUrl + ele.user.photo
            } else if (ele.user.photo == null) {
              ele.user.photo = "assets/images/userAvatar.png"
            }
            var a: any = 0
            ele.activityDate = ele.updatedOn
            this.response.push(ele)
            let dataa: any = this.response.filter((obj, pos, arr) => {
              return arr.map(mapobj => mapobj.jobId).indexOf(obj.jobId) == pos;
            })
            this.response = dataa;
            this.response.forEach((element, i) => {
              if (element.status != 'ACTIVE') {
                this.response.remove(i);
              }
            });
            this.commonvalues.setjobsData(this.response);
          })
        } else if (searchAfterKey == this.previousSearchAfterKey) {
          this.stopscrollFlag = true
          res.data.jobList.forEach(ele => {
            if (ele.user.photo != null) {
              ele.user.photo = AppSettings.photoUrl + ele.user.photo
            } else if (ele.user.photo == null) {
              ele.user.photo = "assets/images/userAvatar.png"
            }
            ele.activityDate = ele.updatedOn
            this.response.push(ele)

            let dataa: any = this.response.filter((obj, pos, arr) => {
              return arr.map(mapobj => mapobj.jobId).indexOf(obj.jobId) == pos;
            })
            this.response = dataa;
            this.response.forEach((element, i) => {
              if (element.status != 'ACTIVE') {
                this.response.remove(i);
              }
            });
            this.commonvalues.setjobsData(this.response);
          })
        } else if (!res.data.searchAfterKey) {
          this.stopscrollFlag = true
        } else if (res.data.searchAfterKey[0] < 0) {
          this.stopscrollFlag = true
        }
      } else if (res.code == "00000" && res.data.jobList.length === 0) {
        this.stopscrollFlag = true
        this.jobFoundStatus = "Couldn't find any Jobs."
        this.previousSearchAfterKey = null;
        this.previousSearchAfterKey1 = null;
        this.obj.searchAfterKey = null;
      }
      if (res.status == 500) {
        this.util.stopLoader()
      }
    })
  }

  coordinateReached: boolean = false
  @HostListener("window:scroll")
  handleScroll() {
    const windowScroll = window.pageYOffset;
    // console.log("windowScroll")
    // console.log(windowScroll)
    if (windowScroll >= 327.5) {
      this.coordinateReached = true;
    } else {
      this.coordinateReached = false;
    }
  }

  onScrollDown() {
    // Skip while a request is in flight: the entry scrollTo/freeze-point
    // scroll events fire this during the first load (empty host), sending a
    // second null-cursor call whose identical cursor then trips stopscrollFlag.
    if (!this.stopscrollFlag && this.businessId && !this.loadAPIcall) {
      this.getJobList();
    }
  }

}
